import { useEffect, useRef } from 'react';

/**
 * Configurable Gmail-style keyboard shortcuts for Imagia Mail.
 *
 * Shortcuts are disabled when focus is in an input/textarea/contenteditable
 * or when the compose modal is open.
 *
 * @param {Object} actions - Map of action name → callback function
 * @param {Object} keymap - Map of action name → key string (from loadShortcuts)
 * @param {Object} options - { composing: boolean }
 */
export default function useKeyboardShortcuts(actions, keymap, { composing = false } = {}) {
  const pendingRef = useRef(null);
  const pendingTimerRef = useRef(null);

  useEffect(() => {
    // Build reverse map: key → action name
    // Separate two-key sequences (g+i) from single keys
    const singleKeyMap = {};   // 'c' → 'compose'
    const shiftKeyMap = {};    // 'I' → 'markRead' (with shift)
    const twoKeyMap = {};      // { first: 'g', second: 'i' } → 'goInbox'

    for (const [action, binding] of Object.entries(keymap)) {
      if (!binding) continue;
      if (binding.includes('+')) {
        const parts = binding.split('+');
        if (parts[0] === 'Shift' && parts.length === 2) {
          shiftKeyMap[parts[1]] = action;
        } else if (parts[0] === 'Ctrl' || parts[0] === 'Meta') {
          // Ctrl/Meta combos handled elsewhere (e.g., Ctrl+Enter in ComposeModal)
        } else if (parts.length === 2) {
          // Two-key sequence like 'g+i'
          if (!twoKeyMap[parts[0]]) twoKeyMap[parts[0]] = {};
          twoKeyMap[parts[0]][parts[1]] = action;
        }
      } else {
        singleKeyMap[binding] = action;
      }
    }

    function handler(e) {
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target.isContentEditable) return;
      if (composing && e.key !== 'Escape') return;

      // Two-key sequence: pending first key
      if (pendingRef.current) {
        const secondMap = twoKeyMap[pendingRef.current];
        pendingRef.current = null;
        clearTimeout(pendingTimerRef.current);
        if (secondMap && secondMap[e.key]) {
          e.preventDefault();
          const action = secondMap[e.key];
          // goInbox/goStarred/etc. → call goToFolder with folder name
          if (action.startsWith('go')) {
            const folderMap = { goInbox: 'inbox', goStarred: 'starred', goSent: 'sent', goDrafts: 'drafts', goArchive: 'archive' };
            actions.goToFolder?.(folderMap[action]);
          } else {
            actions[action]?.();
          }
          return;
        }
        // Not a valid second key — fall through to regular handling
      }

      // Check if this key starts a two-key sequence
      if (twoKeyMap[e.key] && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
        pendingRef.current = e.key;
        pendingTimerRef.current = setTimeout(() => { pendingRef.current = null; }, 1000);
        return;
      }

      // Shift+key combos (but not Cmd+Shift or Ctrl+Shift — those are browser shortcuts)
      if (e.shiftKey && !e.ctrlKey && !e.metaKey && shiftKeyMap[e.key]) {
        e.preventDefault();
        actions[shiftKeyMap[e.key]]?.();
        return;
      }

      // Single key. We deliberately do NOT exclude Shift here: punctuation
      // shortcuts like '?' (Shift+/) or '#' (Shift+3) arrive with shiftKey=true
      // but a distinct e.key, and true Shift+letter combos were already handled
      // above (their e.key is the uppercase letter, which isn't in singleKeyMap).
      if (!e.ctrlKey && !e.metaKey && !e.altKey && singleKeyMap[e.key]) {
        e.preventDefault();
        actions[singleKeyMap[e.key]]?.();
        return;
      }

      // Enter opens the focused email (Gmail parity), same as 'o'.
      if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey && !e.altKey && actions.openEmail) {
        e.preventDefault();
        actions.openEmail();
      }
    }

    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [actions, keymap, composing]);
}
